import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ReportFilter, reportService } from '@/services/reportService';
import {
  Activity,
  AlertCircle,
  ArrowDown,
  ArrowUp,
  BarChart3,
  DollarSign,
  Download,
  Minus,
  Package,
  RefreshCw,
  Settings,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import AdvancedExportDialog from './AdvancedExportDialog';

interface ReportVisualizationProps {
  dateRange: {
    from: string;
    to: string;
  };
}

interface RealTimeMetric {
  label: string;
  value: number;
  previousValue: number;
  unit?: string;
  format?: 'number' | 'currency' | 'percentage';
  trend: 'up' | 'down' | 'neutral';
  changePercent: number;
}

const ReportVisualization: React.FC<ReportVisualizationProps> = ({
  dateRange,
}) => {
  const [selectedReportType, setSelectedReportType] =
    useState<string>('OPERATIONAL');
  const [refreshKey, setRefreshKey] = useState(0);
  const [realTimeEnabled, setRealTimeEnabled] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [showAdvancedExport, setShowAdvancedExport] = useState(false);

  const filters: ReportFilter = {
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
  };

  // Real-time data refresh effect
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (realTimeEnabled) {
      interval = setInterval(() => {
        setRefreshKey(prev => prev + 1);
        setLastUpdated(new Date());
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [realTimeEnabled]);

  // Fetch real-time metrics
  const { data: realTimeData, isLoading: isRealTimeLoading } = useQuery({
    queryKey: ['realtime-metrics', refreshKey],
    queryFn: () => reportService.getRealTimeMetrics(),
    refetchInterval: realTimeEnabled ? 30000 : false, // Refetch every 30 seconds if enabled
    enabled: true,
  });

  // Convert real-time data to metrics format
  const realTimeMetrics: RealTimeMetric[] = realTimeData
    ? [
        {
          label: 'Active AWBs',
          value: realTimeData.activeAWBs,
          previousValue: Math.round(realTimeData.activeAWBs * 0.95), // Mock previous value
          trend: 'up',
          changePercent: 4.1,
          format: 'number',
        },
        {
          label: "Today's Revenue",
          value: realTimeData.todayRevenue,
          previousValue: Math.round(realTimeData.todayRevenue * 0.92), // Mock previous value
          trend: 'up',
          changePercent: 7.9,
          format: 'currency',
        },
        {
          label: 'Delivery Rate',
          value: realTimeData.deliveryRate,
          previousValue: realTimeData.deliveryRate - 1.4, // Mock previous value
          trend: 'up',
          changePercent: 1.5,
          format: 'percentage',
          unit: '%',
        },
        {
          label: 'Processing Time',
          value: realTimeData.avgProcessingTime,
          previousValue: realTimeData.avgProcessingTime + 0.3, // Mock previous value
          trend: 'down',
          changePercent: -11.1,
          format: 'number',
          unit: 'hrs',
        },
      ]
    : [];

  // Fetch operational report data
  const {
    data: operationalData,
    isLoading: isOperationalLoading,
    error: operationalError,
  } = useQuery({
    queryKey: ['operational-report', filters, refreshKey],
    queryFn: () => reportService.generateOperationalReport(filters),
    enabled: selectedReportType === 'OPERATIONAL',
  });

  // Fetch financial report data
  const {
    data: financialData,
    isLoading: isFinancialLoading,
    error: financialError,
  } = useQuery({
    queryKey: ['financial-report', filters, refreshKey],
    queryFn: () => reportService.generateFinancialReport(filters),
    enabled: selectedReportType === 'FINANCIAL',
  });

  // Fetch customer analytics data
  const {
    data: customerData,
    isLoading: isCustomerLoading,
    error: customerError,
  } = useQuery({
    queryKey: ['customer-analytics-report', filters, refreshKey],
    queryFn: () => reportService.generateCustomerAnalyticsReport(filters),
    enabled: selectedReportType === 'CUSTOMER_ANALYTICS',
  });

  // Fetch hub performance data
  const {
    data: hubData,
    isLoading: isHubLoading,
    error: hubError,
  } = useQuery({
    queryKey: ['hub-performance-report', filters, refreshKey],
    queryFn: () => reportService.generateHubPerformanceReport(filters),
    enabled: selectedReportType === 'HUB_PERFORMANCE',
  });

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    setLastUpdated(new Date());
  };

  const formatMetricValue = (metric: RealTimeMetric): string => {
    switch (metric.format) {
      case 'currency':
        return `₹${metric.value.toLocaleString()}`;
      case 'percentage':
        return `${metric.value.toFixed(1)}%`;
      case 'number':
      default:
        return `${metric.value.toLocaleString()}${metric.unit ? ` ${metric.unit}` : ''}`;
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <ArrowUp className='h-4 w-4 text-green-600' />;
      case 'down':
        return <ArrowDown className='h-4 w-4 text-red-600' />;
      case 'neutral':
      default:
        return <Minus className='h-4 w-4 text-gray-600' />;
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      case 'neutral':
      default:
        return 'text-gray-600';
    }
  };

  const handleExport = async (format: 'CSV' | 'JSON' | 'EXCEL') => {
    let data;
    let filename;

    switch (selectedReportType) {
      case 'OPERATIONAL':
        data = operationalData;
        filename = `operational-report-${dateRange.from}-to-${dateRange.to}`;
        break;
      case 'FINANCIAL':
        data = financialData;
        filename = `financial-report-${dateRange.from}-to-${dateRange.to}`;
        break;
      case 'CUSTOMER_ANALYTICS':
        data = customerData;
        filename = `customer-analytics-${dateRange.from}-to-${dateRange.to}`;
        break;
      case 'HUB_PERFORMANCE':
        data = hubData;
        filename = `hub-performance-${dateRange.from}-to-${dateRange.to}`;
        break;
      default:
        return;
    }

    if (data) {
      if (format === 'EXCEL') {
        // For Excel export, we'll use the backend API
        try {
          const response = await reportService.generateReport('temp', {
            format: 'EXCEL',
            filters,
            includeSummary: true,
          });
          // Handle Excel download from backend
        } catch (error) {
          console.error('Excel export failed:', error);
        }
      } else {
        const content = reportService.formatReportData(data, format);
        const blob = new Blob([content], {
          type: format === 'CSV' ? 'text/csv' : 'application/json',
        });
        reportService.downloadFile(blob, `${filename}.${format.toLowerCase()}`);
      }
    }
  };

  const handleAdvancedExport = () => {
    setShowAdvancedExport(true);
  };

  const handleAdvancedExportSubmit = async (options: any) => {
    try {
      const result = await reportService.generateAdvancedReport({
        type: selectedReportType,
        format: options.format,
        filters: {
          ...filters,
          ...options.filterOptions,
        },
        includeCharts: options.includeCharts,
        includeSummary: options.includeSummary,
        includeRawData: options.includeRawData,
        customFileName: options.customFileName,
        emailOptions: options.emailOptions,
        filterOptions: options.filterOptions,
      });

      if (result.downloadUrl) {
        // Open download URL in new tab
        window.open(result.downloadUrl, '_blank');
      }

      if (result.emailSent) {
        console.log('Report emailed successfully');
      }
    } catch (error) {
      console.error('Advanced export failed:', error);
      throw error; // Re-throw to be handled by the dialog
    }
  };

  const getCurrentData = () => {
    switch (selectedReportType) {
      case 'OPERATIONAL':
        return {
          data: operationalData,
          isLoading: isOperationalLoading,
          error: operationalError,
        };
      case 'FINANCIAL':
        return {
          data: financialData,
          isLoading: isFinancialLoading,
          error: financialError,
        };
      case 'CUSTOMER_ANALYTICS':
        return {
          data: customerData,
          isLoading: isCustomerLoading,
          error: customerError,
        };
      case 'HUB_PERFORMANCE':
        return { data: hubData, isLoading: isHubLoading, error: hubError };
      default:
        return { data: null, isLoading: false, error: null };
    }
  };

  const { data: currentData, isLoading, error } = getCurrentData();

  // Chart colors
  const chartColors = {
    primary: '#2563eb',
    secondary: '#7c3aed',
    success: '#059669',
    warning: '#d97706',
    danger: '#dc2626',
    info: '#0891b2',
  };

  // Prepare chart data based on report type
  const getChartData = () => {
    if (!currentData) return null;

    switch (selectedReportType) {
      case 'OPERATIONAL':
        return {
          summary: currentData.summary,
          chartData: currentData.rows.slice(0, 10).map((row, index) => ({
            name: row[0], // AWB Number
            weight: row[7] || 0,
            pieces: row[8] || 0,
            value: row[9] || 0,
          })),
          statusData: Object.entries(
            currentData.summary?.statusBreakdown || {}
          ).map(([status, count]) => ({
            name: status.replace('_', ' '),
            value: count,
            color: getStatusColor(status),
          })),
        };

      case 'FINANCIAL':
        return {
          summary: currentData.summary,
          chartData: currentData.rows.slice(0, 10).map((row, index) => ({
            name: row[1], // Customer
            sales: row[4] || 0,
            purchase: row[5] || 0,
            profit: row[6] || 0,
          })),
          paymentData: Object.entries(
            currentData.summary?.paymentMethodBreakdown || {}
          ).map(([method, count]) => ({
            name: method.replace('_', ' '),
            value: count,
            color: getPaymentMethodColor(method),
          })),
        };

      case 'CUSTOMER_ANALYTICS':
        return {
          summary: currentData.summary,
          chartData: currentData.rows.slice(0, 10).map((row, index) => ({
            name: row[0], // Customer
            awbs: row[1] || 0,
            weight: parseFloat(row[2]) || 0,
            value: parseFloat(row[3]) || 0,
            avgOrder: parseFloat(row[4]) || 0,
          })),
        };

      case 'HUB_PERFORMANCE':
        return {
          summary: currentData.summary,
          chartData: currentData.rows.slice(0, 10).map((row, index) => ({
            name: row[0], // Hub
            awbs: row[1] || 0,
            weight: parseFloat(row[2]) || 0,
            utilization: parseFloat(row[5]?.replace('%', '')) || 0,
            revenue: parseFloat(row[6]) || 0,
          })),
        };

      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      BOOKED: chartColors.info,
      PICKED_UP: chartColors.warning,
      IN_TRANSIT: chartColors.primary,
      OUT_FOR_DELIVERY: chartColors.secondary,
      DELIVERED: chartColors.success,
      RETURNED: chartColors.danger,
      CANCELLED: '#6b7280',
    };
    return colors[status] || chartColors.info;
  };

  const getPaymentMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      CASH: chartColors.success,
      CHEQUE: chartColors.warning,
      ONLINE: chartColors.primary,
      CREDIT: chartColors.secondary,
      PREPAID: chartColors.info,
      COD: chartColors.danger,
    };
    return colors[method] || chartColors.info;
  };

  const chartData = getChartData();

  if (error) {
    return (
      <Card>
        <CardContent className='flex flex-col items-center justify-center py-12'>
          <AlertCircle className='h-12 w-12 text-red-500 mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Data</h3>
          <p className='text-muted-foreground text-center mb-4'>
            Failed to load report data. Please try again.
          </p>
          <Button onClick={handleRefresh}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Real-time Monitoring Section */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Activity className='h-5 w-5 text-blue-600' />
              <CardTitle>Real-time Monitoring</CardTitle>
              {realTimeEnabled && (
                <Badge variant='outline' className='flex items-center gap-1'>
                  <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
                  Live
                </Badge>
              )}
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-muted-foreground'>
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setRealTimeEnabled(!realTimeEnabled)}
                className={
                  realTimeEnabled ? 'bg-green-50 border-green-200' : ''
                }
              >
                <Zap className='h-4 w-4 mr-1' />
                {realTimeEnabled ? 'Disable' : 'Enable'} Live Updates
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
            {realTimeMetrics.map((metric, index) => (
              <Card key={index} className='border-l-4 border-l-blue-500'>
                <CardContent className='p-4'>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='text-sm font-medium text-muted-foreground'>
                      {metric.label}
                    </span>
                    {getTrendIcon(metric.trend)}
                  </div>
                  <div className='flex items-baseline gap-2'>
                    <span className='text-2xl font-bold'>
                      {formatMetricValue(metric)}
                    </span>
                    <span
                      className={`text-sm font-medium ${getTrendColor(metric.trend)}`}
                    >
                      {metric.changePercent > 0 ? '+' : ''}
                      {metric.changePercent.toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    value={Math.abs(metric.changePercent)}
                    className='mt-2 h-1'
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <div className='flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between'>
        <div className='flex gap-4 items-center'>
          <Select
            value={selectedReportType}
            onValueChange={setSelectedReportType}
          >
            <SelectTrigger className='w-48'>
              <SelectValue placeholder='Select report type' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='OPERATIONAL'>Operational Reports</SelectItem>
              <SelectItem value='FINANCIAL'>Financial Reports</SelectItem>
              <SelectItem value='CUSTOMER_ANALYTICS'>
                Customer Analytics
              </SelectItem>
              <SelectItem value='HUB_PERFORMANCE'>Hub Performance</SelectItem>
            </SelectContent>
          </Select>

          <Badge variant='outline' className='flex items-center gap-1'>
            <Target className='h-3 w-3' />
            {selectedReportType.replace('_', ' ')}
          </Badge>
        </div>

        <div className='flex gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => handleExport('CSV')}
            disabled={!currentData}
          >
            <Download className='h-4 w-4 mr-1' />
            CSV
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => handleExport('JSON')}
            disabled={!currentData}
          >
            <Download className='h-4 w-4 mr-1' />
            JSON
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => handleExport('EXCEL')}
            disabled={!currentData}
          >
            <Download className='h-4 w-4 mr-1' />
            Excel
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleAdvancedExport}
            disabled={!currentData}
          >
            <Settings className='h-4 w-4 mr-1' />
            Advanced
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className='animate-pulse'>
              <CardHeader>
                <div className='h-4 bg-muted rounded w-3/4'></div>
                <div className='h-3 bg-muted rounded w-1/2'></div>
              </CardHeader>
              <CardContent>
                <div className='h-64 bg-muted rounded'></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : chartData ? (
        <>
          {/* Summary Cards */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
            {selectedReportType === 'OPERATIONAL' && (
              <>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total AWBs
                    </CardTitle>
                    <Package className='h-4 w-4 text-blue-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      {chartData.summary?.totalAWBs?.toLocaleString() || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Weight
                    </CardTitle>
                    <TrendingUp className='h-4 w-4 text-green-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      {chartData.summary?.totalWeight?.toFixed(1) || 0} kg
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Pieces
                    </CardTitle>
                    <Package className='h-4 w-4 text-purple-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      {chartData.summary?.totalPieces?.toLocaleString() || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Value
                    </CardTitle>
                    <DollarSign className='h-4 w-4 text-orange-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      ₹{chartData.summary?.totalValue?.toLocaleString() || 0}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {selectedReportType === 'FINANCIAL' && (
              <>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Revenue
                    </CardTitle>
                    <DollarSign className='h-4 w-4 text-green-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      ₹{chartData.summary?.totalRevenue?.toLocaleString() || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Costs
                    </CardTitle>
                    <TrendingUp className='h-4 w-4 text-red-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      ₹{chartData.summary?.totalCosts?.toLocaleString() || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Profit
                    </CardTitle>
                    <TrendingUp className='h-4 w-4 text-blue-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      ₹{chartData.summary?.totalProfit?.toLocaleString() || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Avg Order Value
                    </CardTitle>
                    <DollarSign className='h-4 w-4 text-purple-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      ₹{chartData.summary?.averageOrderValue?.toFixed(0) || 0}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {selectedReportType === 'CUSTOMER_ANALYTICS' && (
              <>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Customers
                    </CardTitle>
                    <Users className='h-4 w-4 text-blue-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      {chartData.summary?.totalCustomers?.toLocaleString() || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Active Customers
                    </CardTitle>
                    <Users className='h-4 w-4 text-green-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      {chartData.summary?.activeCustomers?.toLocaleString() ||
                        0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Avg Customer Value
                    </CardTitle>
                    <DollarSign className='h-4 w-4 text-purple-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      ₹
                      {chartData.summary?.averageCustomerValue?.toFixed(0) || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Top Customer
                    </CardTitle>
                    <TrendingUp className='h-4 w-4 text-orange-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-sm font-bold truncate'>
                      {chartData.summary?.topCustomersByValue?.[0]?.name ||
                        'N/A'}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      ₹
                      {chartData.summary?.topCustomersByValue?.[0]?.value?.toLocaleString() ||
                        0}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {selectedReportType === 'HUB_PERFORMANCE' && (
              <>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Hubs
                    </CardTitle>
                    <Package className='h-4 w-4 text-blue-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      {chartData.summary?.totalHubs?.toLocaleString() || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Total Throughput
                    </CardTitle>
                    <TrendingUp className='h-4 w-4 text-green-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      {chartData.summary?.totalThroughput?.toLocaleString() ||
                        0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Avg Utilization
                    </CardTitle>
                    <BarChart3 className='h-4 w-4 text-purple-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-2xl font-bold'>
                      {chartData.summary?.averageUtilization?.toFixed(1) || 0}%
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle className='text-sm font-medium'>
                      Top Hub
                    </CardTitle>
                    <TrendingUp className='h-4 w-4 text-orange-600' />
                  </CardHeader>
                  <CardContent>
                    <div className='text-sm font-bold truncate'>
                      {chartData.summary?.topPerformingHub || 'N/A'}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Charts */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* Main Chart */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <BarChart3 className='h-5 w-5' />
                  {selectedReportType === 'OPERATIONAL' && 'Top AWBs by Weight'}
                  {selectedReportType === 'FINANCIAL' && 'Revenue vs Costs'}
                  {selectedReportType === 'CUSTOMER_ANALYTICS' &&
                    'Top Customers by Value'}
                  {selectedReportType === 'HUB_PERFORMANCE' &&
                    'Hub Performance'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='h-80'>
                  <ResponsiveContainer width='100%' height='100%'>
                    {selectedReportType === 'FINANCIAL' ? (
                      <BarChart data={chartData.chartData}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='name' fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip formatter={value => [`₹${value}`, '']} />
                        <Legend />
                        <Bar
                          dataKey='sales'
                          fill={chartColors.success}
                          name='Sales'
                        />
                        <Bar
                          dataKey='purchase'
                          fill={chartColors.danger}
                          name='Purchase'
                        />
                        <Bar
                          dataKey='profit'
                          fill={chartColors.primary}
                          name='Profit'
                        />
                      </BarChart>
                    ) : (
                      <BarChart data={chartData.chartData}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='name' fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip />
                        <Bar
                          dataKey={
                            selectedReportType === 'OPERATIONAL'
                              ? 'weight'
                              : selectedReportType === 'CUSTOMER_ANALYTICS'
                                ? 'value'
                                : 'awbs'
                          }
                          fill={chartColors.primary}
                        />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Distribution Chart */}
            {selectedReportType === 'OPERATIONAL' && chartData.statusData && (
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <PieChart className='h-5 w-5' />
                    Status Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='h-80'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <PieChart>
                        <Pie
                          data={chartData.statusData}
                          cx='50%'
                          cy='50%'
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={80}
                          fill='#8884d8'
                          dataKey='value'
                        >
                          {chartData.statusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedReportType === 'FINANCIAL' && chartData.paymentData && (
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <PieChart className='h-5 w-5' />
                    Payment Method Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='h-80'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <PieChart>
                        <Pie
                          data={chartData.paymentData}
                          cx='50%'
                          cy='50%'
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={80}
                          fill='#8884d8'
                          dataKey='value'
                        >
                          {chartData.paymentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedReportType === 'CUSTOMER_ANALYTICS' && (
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <LineChart className='h-5 w-5' />
                    Customer Order Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='h-80'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <LineChart data={chartData.chartData}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='name' fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip />
                        <Line
                          type='monotone'
                          dataKey='awbs'
                          stroke={chartColors.primary}
                          name='AWBs'
                        />
                        <Line
                          type='monotone'
                          dataKey='avgOrder'
                          stroke={chartColors.secondary}
                          name='Avg Order'
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedReportType === 'HUB_PERFORMANCE' && (
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <AreaChart className='h-5 w-5' />
                    Hub Utilization
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='h-80'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <AreaChart data={chartData.chartData}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='name' fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip
                          formatter={value => [`${value}%`, 'Utilization']}
                        />
                        <Area
                          type='monotone'
                          dataKey='utilization'
                          stroke={chartColors.primary}
                          fill={chartColors.primary}
                          fillOpacity={0.3}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      ) : (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <BarChart3 className='h-12 w-12 text-muted-foreground mb-4' />
            <h3 className='text-lg font-semibold mb-2'>No Data Available</h3>
            <p className='text-muted-foreground text-center'>
              No data found for the selected date range and filters
            </p>
          </CardContent>
        </Card>
      )}

      {/* Advanced Export Dialog */}
      {showAdvancedExport && (
        <AdvancedExportDialog
          isOpen={showAdvancedExport}
          onClose={() => setShowAdvancedExport(false)}
          reportData={currentData}
          reportType={selectedReportType}
          onExport={handleAdvancedExportSubmit}
        />
      )}
    </div>
  );
};

export default ReportVisualization;
